/**
 * Publishes each sticky column's rendered height as `--sticky-content-height`.
 *
 * CSS can read an element's own height nowhere, and the product details column
 * needs it: while the column is shorter than the viewport it should pin under
 * the header, but once it grows taller (accordion rows opened, long copy) it has
 * to scroll all the way into view first and then pin with its bottom just above
 * the viewport edge - otherwise its lower half is unreachable. The offset maths
 * live in product-information-content.liquid; this only keeps the height fresh.
 *
 * With the variable unset - script blocked, ResizeObserver missing - the CSS
 * falls back to pinning under the header, which is the behaviour it had before.
 */
(() => {
  const SELECTOR = '.sticky-content--desktop';

  if (!('ResizeObserver' in window)) return;

  const publishHeight = (element, { force = false } = {}) => {
    // Only ever measure the collapsed column. An opened accordion row must not
    // move the offset: while the column is pinned, changing the offset releases
    // the pin and snaps the column up by however far it was being held - 170px
    // at 200px of scroll, and worse the deeper the page is scrolled. Holding the
    // offset still makes an opened row push the content below it down instead.
    if (!force && element.querySelector('details[open]')) return;

    const { height } = element.getBoundingClientRect();

    element.style.setProperty('--sticky-content-height', `${Math.ceil(height)}px`);
  };

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) publishHeight(entry.target);
  });

  const observe = (root) => {
    for (const element of root.querySelectorAll(SELECTOR)) {
      // First measurement is unconditional, so a row set to open by default is
      // still part of the baseline.
      publishHeight(element, { force: true });
      observer.observe(element);
    }
  };

  observe(document);

  // Section re-renders (theme editor, variant morphs) can swap the column out.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) observe(event.target);
  });
})();
